import { Button, Helper, Loader, MenuBar, StandaloneIcon, Sup, TruncateToolTip, Header } from 'Components';
import needDollarSign from 'constants/need-currency';
import ID from 'helpers/id';
import moneyFormatter from 'helpers/money-formatter';
import { capitalToDashedLower as classNamer } from 'helpers/regex';
import { arrayMove } from 'helpers/array-helpers';
import { handleColumnSave } from 'helpers/column-save';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import './Table.css';

// TODO: replace with menuIcons const
const icons = ['edit', 'file_copy', 'note_add', 'add_shopping_cart', 'email', 'publish', 'delete'];
const labels = ['edit', 'copy', 'new job', 'order supplies', 'share', 'export to excel', 'delete'];
const classNames = ['', '', '', '', '', 'icon-flipped', ''];
const showLockeds = [false, true, true, true, true, true, false];

const formatCell = (key, row) => {
  const hasSuperScript = String(row[key]).includes('&sup');
  const isAmount = needDollarSign.includes(key);
  const needToolTipList = ['Caption', 'Company'].includes(key);



  if (hasSuperScript) {
    return <Sup>{row[key]}</Sup>;
  }

  if (isAmount) {
    return moneyFormatter(row[key]);
  }

  if (key === 'IsOptout') {
    return row[key] ? i18n.t('common:Deny') : i18n.t('common:Allow');
  }

  if (key === 'AllowJobEdit' && row[key] !== undefined) {
    return row[key] ? i18n.t('common:Allow') : i18n.t('common:Deny');
  }

  if (key === 'AllowSplitReports' && row[key] !== undefined) {
    return row[key] ? i18n.t('common:Allow') : i18n.t('common:Deny');
  }

  if (needToolTipList) {
    return <TruncateToolTip text={row[key]} />;
  }

  return (row[key] !== undefined) ? row[key] : '';
};

const menubarInitialState = icons.map((icon, i) => ({
  label: labels[i],
  icon: icons[i],
  show: true,
  className: classNames[i],
  showLocked: showLockeds[i],
}));

const Table = (props) => {
  // Drag and Drop
  const [dragIndexFrom, setDragIndexFrom] = useState(undefined);
  const [dragIndexTo, setDragIndexTo] = useState(undefined);

  const onDragStart = i => setDragIndexFrom(i);

  const onDragOver = (i) => {
    if (i !== dragIndexTo) {
      setDragIndexTo(i);
    }
  };

  const onDragEnd = () => {
    setDragIndexFrom(undefined);
    setDragIndexTo(undefined);
    handleColumnSave(
      arrayMove(props.keys, dragIndexFrom, dragIndexTo),
      props.viewCode, props.setAppState,
    );
  };

  const formatHeaders = (key, keyPairs, i) => {
    let headerValue = <Sup>{keyPairs[key]}</Sup>;
    if (typeof keyPairs[key] !== 'string') {
      headerValue = keyPairs[key];
    }

    return (
      <Header
        padLeft={i === 0}
        key={key}
        keyVal={key}
        sort={props.sort}
        onDragStart={() => onDragStart(i)}
        onDragOver={() => onDragOver(i)}
        onDragEnd={onDragEnd}
        isHoverActive={dragIndexTo === i}
        isDraggable={props.isDraggable}
        className={`th th__${classNamer(key)} ${i === dragIndexTo ? 'th__onhover--active' : ''}`}
        sortable={props.isDraggable}
        handleSortClick={props.handleSortClick}
        hasManualSort
      >
        {headerValue}
      </Header>
    );
  };


  let columns;
  if (!props.hideHeaders) {
    if (props.keys === undefined) {
      columns = [];
    } else {
      columns = props.keys.map((key, i) => (formatHeaders(key, props.keyPairs, i)));
    }
    if (props.showOptions || props.showOptionsHeader) {
      // apparently the following indent style is impossible with AirBnb eslint settings
      /* eslint-disable-next-line */
      columns.push(
        <th key="$more_vert" className="table__fixed-col">
          <StandaloneIcon
            onClick={() => props.setAppState({ settingTableCode: props.viewCode })}
            icon="settings"
          />
        </th>);
    }
    if (props.showFlag) columns.unshift(<th key="$turned_in" />);
  }

  let rows;
  if (props.data === undefined) {
    rows = [];
  } else {
    rows = props.data.map((row, i) => {
      const tds = props.keys.map((key, j) => {
        const cell = (
          formatCell(key, row)
        );
        if (j === 0) {
          return (
            <td key={key} data-id={i} className={key === 'Status' ? 'table__row__td--bold' : ''}>
              <Helper text={props.firstColumnHelperText}>
                {cell}
              </Helper>
            </td>
          );
        }

        return (
          <td key={key} data-id={i} className={key === 'Status' ? 'table__row__td--bold' : ''}>
            {cell}
          </td>
        );
      });
      if (props.showOptions || props.showMore) {
        // eslint-disable-next-line function-paren-newline
        tds.push(
          // eslint-disable-next-line
          <Helper key="$more_vert" text={props.moreVertHelperText}>
            <td className="table__fixed-col">
              <Button onClick={e => props.onConfigClick(e, i)}>
                <i className="material-icons td__orange">more_vert</i>
              </Button>
            </td>
          </Helper>);
        tds.push( //eslint-disable-line
          <td key="$menu" className="table__menu">
            <MenuBar
              tableIndex={i}
              onClick={props.onMenuClick}
              show={i === props.menuRow}
              isLocked={row.isLocked}
              icons={props.menuBar}
            />
          </td>);
      } if (!props.showOptions && props.showOptionsHeader) {
        tds.push(<td key="$more_vert" className="table__fixed-col" />);
      }
      if (props.showFlag) tds.unshift(<td key="$turned_in"><Button onClick={e => props.onFlagClick(e, i)}><i className="material-icons">turned_in</i></Button></td>);
      const trProps = props.onRowClick ? {
        role: 'link',
        onClick: e => props.onRowClick(e, i),
      } : {};

      return (
        <tr
          {...trProps}
          key={ID()}
        >
          {tds}
        </tr>);
    });
  }

  return (
    <div className="table-wrapper">
      {props.isLoading && <Loader isSolidWhite={props.isSolidWhiteLoader} />}
      <table className="table">
        {
          !props.hideHeaders &&
          <thead>
            <tr>{columns}</tr>
          </thead>
        }
        <tbody {...(props.isDraggable && { onDragOver: e => props.onDragOver(e) })}>
          {rows}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  onRowClick: PropTypes.func,
  onConfigClick: PropTypes.func, // eslint-disable-line react/no-unused-prop-types
  onSettingClick: PropTypes.func,
  onFlagClick: PropTypes.func,
  showOptions: PropTypes.bool,
  showFlag: PropTypes.bool,
  showMore: PropTypes.bool,
  showOptionsHeader: PropTypes.bool,
  hideHeaders: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasMenuOpenOnClick: PropTypes.bool,
  keys: PropTypes.arrayOf(PropTypes.string),
  keyPairs: PropTypes.shape({}),
  data: PropTypes.arrayOf(PropTypes.object),
  menuRow: PropTypes.number,
  menuBar: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    show: PropTypes.bool,
    className: PropTypes.string,
    showLocked: PropTypes.bool,
  })),
  isDraggable: PropTypes.bool,
  onMenuClick: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  activeHeader: PropTypes.number,
  sortDirection: PropTypes.arrayOf(['desc', 'asc']),
  isSolidWhiteLoader: PropTypes.bool,
  moreVertHelperText: PropTypes.string,
  firstColumnHelperText: PropTypes.string,
  viewCode: PropTypes.string,
  setAppState: PropTypes.func,
  sort: PropTypes.string,
  handleSortClick: PropTypes.func,
};

Table.defaultProps = {
  activeHeader: 0,
  showOptions: false,
  showMore: false,
  showFlag: false,
  showOptionsHeader: false,
  hideHeaders: false,
  isLoading: false,
  isDraggable: false,
  menuBar: menubarInitialState,
  moreVertHelperText: '',
  firstColumnHelperText: '',
  onFlagClick: () => null,
  onConfigClick: () => { },
  onSettingClick: () => { },
  onRowClick: () => { },
  onMenuClick: () => { },
  onDragOver: () => { },
  onDragStart: () => { },
  onDragEnd: () => { },
};

export default Table;



// WEBPACK FOOTER //
// ./src/components/Table/Table.jsx